// Pure helpers for showing a Language row to a person. Stored names are
// canonical English ("Mandarin"); everything a user reads goes through here.
// No GraphQL import on purpose: node-environment tests and SSR code use these.
//
// Intl.DisplayNames is not cheap to construct, and the pickers label a few
// hundred rows at once (#768), so instances are kept per locale and every
// answer is memoized — the inputs never change within a session.

const displayNamesByLocale = new Map<string, Intl.DisplayNames | null>();
const displayNameCache = new Map<string, string>();
const endonymCache = new Map<string, string>();

function displayNamesFor(locale: string): Intl.DisplayNames | null {
  if (!displayNamesByLocale.has(locale)) {
    let instance: Intl.DisplayNames | null = null;
    try {
      instance = new Intl.DisplayNames([locale], { type: "language", fallback: "none" });
    } catch {
      instance = null;
    }
    displayNamesByLocale.set(locale, instance);
  }
  return displayNamesByLocale.get(locale) ?? null;
}

function nameFrom(instance: Intl.DisplayNames | null, code: string, englishName: string, capitalizeIn: string): string {
  if (!instance) return englishName;
  try {
    const name = instance.of(code);
    if (!name || name.toLowerCase() === code.toLowerCase()) return englishName;
    return name.charAt(0).toLocaleUpperCase(capitalizeIn) + name.slice(1);
  } catch {
    return englishName;
  }
}

// Whether ICU can actually speak `code` as a locale. Intl.DisplayNames([code])
// never refuses an unknown locale — it quietly falls back to the runtime's
// default one, which would label a Hakka or Egyptian-Arabic row in whatever
// language the visitor's device is set to instead of in itself.
function localeSupported(code: string): boolean {
  try {
    return Intl.DisplayNames.supportedLocalesOf([code]).length > 0;
  } catch {
    return false;
  }
}

// Localized name for a Language. English UIs keep the canonical name verbatim
// (Intl would say "Chinese" for zh), other locales get Intl.DisplayNames'
// name, capitalized (Romance languages return it lowercase). Falls back to the
// English name when Intl has no data for the code or the runtime lacks the API.
export function languageDisplayName(code: string, englishName: string, locale: string): string {
  if (!locale || locale.toLowerCase().startsWith("en")) return englishName;
  const key = `${locale}|${code}|${englishName}`;
  const cached = displayNameCache.get(key);
  if (cached !== undefined) return cached;
  const name = nameFrom(displayNamesFor(locale), code, englishName, locale);
  displayNameCache.set(key, name);
  return name;
}

// The language's own name for itself ("Français", "日本語", "العربية"): the
// native-language picker labels every row this way, so a Spanish speaker on a
// Japanese UI still finds "Español". English name when ICU has no locale data
// for the code (most three-letter codes) or no name.
export function languageEndonym(code: string, englishName: string): string {
  if (!code) return englishName;
  const key = `${code}|${englishName}`;
  const cached = endonymCache.get(key);
  if (cached !== undefined) return cached;
  const name = localeSupported(code) ? nameFrom(displayNamesFor(code), code, englishName, code) : englishName;
  endonymCache.set(key, name);
  return name;
}

// Options with their localized label, sorted by that label under the locale's
// collation — so the order a user sees matches the names they read.
export function sortedLanguageOptions<T extends { code: string; name: string }>(
  options: T[],
  locale: string,
): (T & { label: string })[] {
  return options
    .map((o) => ({ ...o, label: languageDisplayName(o.code, o.name, locale) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale || undefined));
}
